import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { axisBottom, axisLeft } from 'd3-axis';
import { scaleTime, select, scaleBand, scaleSequential, scaleLinear, range, axisTop, utcHour, timeHour, scaleQuantize } from 'd3';
import { DateTime } from 'luxon';

import { interpolateColour } from '../utils/interpolateColour';

interface StyledSvgProps {
    fontSize: number;
    tickSize: number;
    offsetToMidnight: number;
    displayTimeLegend: boolean;
}
const StyledSvg = styled.svg<StyledSvgProps>`
    position: absolute;
    left: 0;
    text {
        font-size: ${({ fontSize }) => fontSize}px;
    }
    .x-axis-date,
    .x-axis-time {
        text {
            stroke: white;
            stroke-width: 4;
            paint-order: stroke;
        }
    }
    .x-axis-time {
        text {
            display: ${({ displayTimeLegend }) => (displayTimeLegend ? 'initial' : 'none')};
        }
    }
    .x-axis-date {
        .tick {
            line {
                display: none;
            }
            text {
                transform: translateY(-${({ tickSize }) => tickSize}px);
                dominant-baseline: hanging;
            }
        }
        .tick:nth-child(24n + ${({ offsetToMidnight }) => offsetToMidnight + 2}) {
            line {
                display: initial;
            }
        }
    }
    .grid-lines {
        &.grid-day-line {
            line {
                display: none;
            }
            .tick:nth-child(24n + ${({ offsetToMidnight }) => offsetToMidnight + 2}) {
                line {
                    display: initial;
                    stroke: white;
                    stroke-width: 1;
                }
            }
        }
        line {
            stroke-dasharray: 2;
            stroke-width: 0.5;
            stroke: #ebebeb;
        }
    }
`;

interface AxesProps {
    fullWidth: number;
    fullHeight: number;
    xAxisData: any[];
    yAxisData: any[];
    canvasWidth: number;
    canvasHeight: number;
    fontSizeRatio: number;
    fontSize: number;
    tickSizeInner: number;
    tickSize: number;
    leftOffset: number;
    timezone: string;
    yAxisUnit: string;
    yAxisDecimalPlaces: number;
    minValue: number;
    maxValue: number;
    topOffset: number;
    specdenUnits: string;
    displayGrid: boolean;
    displayTimeLegend: boolean;
}

const Axes = ({
    canvasHeight,
    canvasWidth,
    fontSizeRatio,
    fontSize,
    fullHeight,
    fullWidth,
    leftOffset,
    tickSize,
    tickSizeInner,
    timezone,
    xAxisData,
    yAxisData,
    yAxisDecimalPlaces,
    yAxisUnit,
    minValue,
    maxValue,
    topOffset,
    specdenUnits,
    displayGrid,
    displayTimeLegend,
}: AxesProps) => {
    const [offsetToMidnight, setOffsetToMidnight] = useState(0);
    const bottomAxisRef = useRef<SVGElement>();

    useLayoutEffect(() => {
        const svg = select(bottomAxisRef.current!).attr('width', fullWidth).attr('height', fullHeight);

        /**
         * Render X-Time axis
         */
        const xscale = scaleTime()
            .domain([DateTime.fromISO(xAxisData[0]).toJSDate(), DateTime.fromISO(xAxisData[xAxisData.length - 1]).toJSDate()])
            .range([0, canvasWidth]);

        const rangeOfHoursBetweenStartAndEndDate = timeHour.range(
            DateTime.fromISO(xAxisData[0]).toJSDate(),
            DateTime.fromISO(xAxisData[xAxisData.length - 1]).toJSDate()
        );
        const xAxisResponsiveness = parseInt(((rangeOfHoursBetweenStartAndEndDate.length * 80) / (canvasWidth / fontSizeRatio)).toString());
        const quantizeHourSteps = scaleQuantize().domain([0, 24]).range([2, 4, 6, 8, 12, 24]);
        const xTimeAxis = axisBottom(xscale)
            .tickValues(
                rangeOfHoursBetweenStartAndEndDate.filter((date) => {
                    const hour = DateTime.fromJSDate(date, { zone: timezone }).get('hour');
                    return !(hour % quantizeHourSteps(xAxisResponsiveness));
                })
            )
            .tickSizeInner(tickSizeInner)
            .tickSizeOuter(tickSize + tickSizeInner)
            .tickFormat((date: Date) => DateTime.fromJSDate(date, { zone: timezone }).toFormat('HH:mm'));
        if (!displayTimeLegend) {
            xTimeAxis.tickSize(0);
        }

        let tickSet = false;
        const xDayAxis = axisBottom(xscale)
            .ticks(utcHour.every(1))
            .tickSize(tickSize)
            .tickFormat((date: Date, i) => {
                const dateTime = DateTime.fromJSDate(date, { zone: timezone });
                if (dateTime.toFormat('HHmm') === '0000' && tickSet === false) {
                    setOffsetToMidnight(i);
                    tickSet = true;
                }
                if (dateTime.toFormat('HHmm') === '1200') {
                    return dateTime.toFormat('ccc dd');
                }
                return '';
            });
        svg.append('g')
            .attr('class', 'x-axis-date')
            .attr('transform', `translate(${leftOffset}, ${canvasHeight + topOffset})`)
            .call(xDayAxis)
            .selectAll('text')
            .attr('dy', 0);
        svg.append('g')
            .attr('class', 'x-axis-time')
            .attr('transform', `translate(${leftOffset}, ${canvasHeight + tickSize + topOffset})`)
            .call(xTimeAxis);

        /**
         * Render Y-axis
         */
        const yscale = scaleBand<number>().domain(yAxisData).range([0, canvasHeight]);
        const yAxisResponsiveness = parseInt(((yAxisData.length * 40) / (canvasHeight / fontSizeRatio)).toString()) || 1;
        const yAxis = axisLeft(yscale)
            .tickFormat((d) => `${d.toFixed(yAxisDecimalPlaces)}${yAxisUnit}`)
            .tickValues(
                yscale.domain().filter((_d, i) => {
                    return !(i % yAxisResponsiveness);
                })
            );
        svg.append('g').attr('transform', `translate(${leftOffset}, ${topOffset})`).call(yAxis);

        /**
         * render Grid
         */
        if (displayGrid) {
            const yAxisGrid = yAxis.tickFormat(() => ``).tickSize(-canvasWidth);
            const xTimeGrid = xTimeAxis.tickFormat(() => '').tickSize(-canvasHeight);
            const xDayAxisGrid = xDayAxis.tickFormat(() => '').tickSize(-canvasHeight);
            svg.append('g').attr('transform', `translate(${leftOffset}, ${topOffset})`).attr('class', 'grid-lines').call(yAxisGrid);
            svg.append('g')
                .attr('transform', `translate(${leftOffset}, ${canvasHeight + topOffset})`)
                .attr('class', 'grid-lines')
                .call(xTimeGrid);
            svg.append('g')
                .attr('transform', `translate(${leftOffset}, ${canvasHeight + topOffset})`)
                .attr('class', 'grid-lines grid-day-line')
                .call(xDayAxisGrid);
        }

        /**
         * Render colour legend
         */
        const colourScale = scaleSequential().domain([minValue, maxValue]).interpolator(interpolateColour);
        const colourXScale = scaleLinear().domain([minValue, maxValue]).range([0, canvasWidth]);
        const colourScaleTicks = Math.round(Math.max(canvasWidth / (70 * fontSizeRatio), 2));
        const colourAxisTop = axisTop(colourXScale)
            .ticks(colourScaleTicks)
            .tickFormat((val, i) => (i === 0 && val.toString() !== '0' ? `< ${val}` : val.toString()));
        const step = maxValue / 100;
        const data = range(minValue, maxValue, (maxValue - minValue) / 100);
        svg.selectAll('rect')
            .data(data)
            .enter()
            .append('rect')
            .attr('x', (d) => Math.floor(colourXScale(d)))
            .attr('y', 0)
            .attr('height', topOffset / 2)
            .attr('width', (d) => Math.floor(colourXScale(d + step)) - Math.floor(colourXScale(d)) + step + 1)
            .attr('transform', `translate(${leftOffset}, ${topOffset / 2 - 2})`)
            .attr('fill', (d) => colourScale(d));
        svg.append('text')
            .attr('dominant-baseline', 'hanging')
            .attr('font-style', 'italic')
            .attr('text-anchor', 'middle')
            .attr('fill', 'white')
            .attr('x', canvasWidth / 2)
            .attr('transform', `translate(${leftOffset}, ${topOffset / 2 + 4})`)
            .text(topOffset === 0 ? '' : specdenUnits); // hide when topOffset is 0
        svg.append('g')
            .attr('class', 'x-axis-colour')
            .attr('transform', `translate(${leftOffset}, ${topOffset / 2 - 2})`)
            .call(colourAxisTop);

        return () => {
            svg.html('');
        };
    }, [xAxisData, yAxisData, canvasHeight, canvasWidth, leftOffset, fullWidth, fullHeight, yAxisUnit, yAxisDecimalPlaces, displayGrid]);

    return (
        <StyledSvg
            displayTimeLegend={displayTimeLegend}
            offsetToMidnight={offsetToMidnight}
            tickSize={tickSize}
            fontSize={fontSize}
            ref={bottomAxisRef as any}
        />
    );
};

export default Axes;
